import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { loginUser, registerUser, verifyOtp } from '../../../redux/actions/authAction'
import { loginUserWithOtp } from '../../../redux/actions/userAction'
import { SET_AUTH_LOADER, SET_ONBOARDING_EMAIL } from '../../../redux/types'
import { OTP_LENGTH, OTP_VALID_DURATION, characterConst } from '../../../utils/constant'
import OtpSection from '../../auth/otp-section'
import { trackConversion, getTrackingParams, trackOnboarding } from '../../../utils/analytics'
import { hapticSuccess, hapticError } from '../../../utils/haptics'
import { CheckIcon } from './icons'
import { StepTitle } from '../stepContext'
import { referralSignupParams } from '../../../utils/referral'
import { apiErrorMessage } from 'i18n/apiError'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const SPAM_HINT_DELAY_MS = 20000

const GateStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { experience, preferenceType, name } = AppUseSelector(({ onboarding }) => onboarding)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showOtp, setShowOtp] = useState(false)
  const [isExistingAccount, setIsExistingAccount] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [timer, setTimer] = useState(OTP_VALID_DURATION)
  const [isHidden, setIsHidden] = useState(false)
  const [showSpamHint, setShowSpamHint] = useState(false)
  // Once the code is accepted this question is done, and the flow is carried
  // by the question revealed below it. Leaving the code box and its button on
  // screen would leave the user with a stale control to press.
  const [verified, setVerified] = useState(false)

  const tookTest = experience === 'yes'

  useEffect(() => {
    trackOnboarding('signup_gate_viewed', { took_test: tookTest })
  }, [])

  useEffect(() => {
    if (!showOtp || timer <= 0) return
    const interval = setInterval(() => setTimer((seconds) => seconds - 1), 1000)
    return () => clearInterval(interval)
  }, [showOtp, timer])

  useEffect(() => {
    if (!showOtp) return
    setShowSpamHint(false)
    const timeout = setTimeout(() => setShowSpamHint(true), SPAM_HINT_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [showOtp])

  const requestOtp = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const cleanEmail = email.trim().toLowerCase()
    if (!EMAIL_REGEX.test(cleanEmail)) {
      setEmailError(t('gate.invalidEmail'))
      hapticError()
      return
    }
    setEmail(cleanEmail)
    setEmailError(null)
    setLoading(true)
    try {
      const trackingParams = getTrackingParams()
      // Persist the writing-system choice from WritingStep at account creation.
      // Without this the backend defaults every new account to Simplified,
      // so Traditional users saw Simplified characters after signing up.
      //
      // This gate runs BEFORE the script question, so what goes up here is
      // only the bootstrap default — WritingStep corrects it afterwards with
      // SaveCharacter.
      const character = preferenceType?.includes('Traditional')
        ? characterConst.traditional
        : characterConst.simplified
      const res: any = await registerUser({
        email: cleanEmail,
        ...trackingParams,
        // Give-get referral: the code from a /r/ link clicked in the last 30 days, if any.
        ...referralSignupParams(),
        character,
        // Only present when the user is coming back through a part-finished
        // flow. The name is asked *after* this question, so normally the
        // backend invents one from the email address and NameStep overwrites
        // it with the confirmed name.
        ...(name ? { name } : {}),
      })
      if (res?.status === 200) {
        setIsExistingAccount(false)
        setShowOtp(true)
        setTimer(OTP_VALID_DURATION)
        trackOnboarding('otp_requested', { account: 'new' })
      } else if (res?.status === 400 && (res?.code === 'USER_ALREADY_EXISTS' || /exist/i.test(res?.message || ''))) {
        // Existing account — send a login code instead and continue seamlessly
        const loginRes: any = await loginUser({ email: cleanEmail })
        if (loginRes?.status === 429) {
          setEmailError(apiErrorMessage(loginRes, 'onboarding:gate.tooManyCodes'))
        } else {
          setIsExistingAccount(true)
          setShowOtp(true)
          setTimer(OTP_VALID_DURATION)
          trackOnboarding('otp_requested', { account: 'existing' })
        }
      } else {
        setEmailError(apiErrorMessage(res, 'onboarding:gate.genericError'))
      }
    } catch (err: any) {
      setEmailError(apiErrorMessage(err, 'onboarding:gate.genericError'))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setTimer(OTP_VALID_DURATION)
    setOtp('')
    setOtpError(null)
    try {
      const res: any = await loginUser({ email })
      trackOnboarding('otp_resent', {})
      if (res?.status === 429) {
        setOtp('')
        setTimer(0)
        setOtpError(apiErrorMessage(res, 'onboarding:gate.tooManyCodes'))
        setIsHidden(true)
      }
    } catch (err: any) {
      setOtpError(apiErrorMessage(err, 'onboarding:gate.resendFailed'))
      setIsHidden(true)
    }
  }

  const handleOtpSubmit = async () => {
    setOtpError(null)
    if (otp.length !== OTP_LENGTH) {
      setOtpError(t('gate.codeLength', { length: OTP_LENGTH }))
      return
    }
    try {
      const response: any = await verifyOtp({ email, otp })
      if (response?.status === 200 && response?.data) {
        loginUserWithOtp({ userDetails: response?.data || null, isSignup: !isExistingAccount })
        dispatch({ type: SET_AUTH_LOADER, payload: false })
        trackOnboarding('otp_verified', { account: isExistingAccount ? 'existing' : 'new' })
        // The name question is revealed by the next() below and seeds itself
        // from this, so the address has to outlive this component's state.
        dispatch({ type: SET_ONBOARDING_EMAIL, payload: email })
        hapticSuccess()
        if (!isExistingAccount) {
          trackConversion({ event: 'sign_up', value: 0, user_id: response?.data?.id })
        }
        setVerified(true)
        next()
        return
      }
      dispatch({ type: SET_AUTH_LOADER, payload: false })
      trackOnboarding('otp_failed', { status: response?.status })
      hapticError()
      if (response?.status === 429) {
        setOtp('')
        setTimer(0)
        setIsHidden(true)
        setOtpError(apiErrorMessage(response, 'onboarding:gate.tooManyAttempts'))
        return
      }
      setOtp('')
      setOtpError(apiErrorMessage(response, 'onboarding:gate.codeMismatch'))
    } catch (err: any) {
      dispatch({ type: SET_AUTH_LOADER, payload: false })
      setOtpError(apiErrorMessage(err, 'onboarding:gate.unknownError'))
    }
  }

  const handleBackToEmail = () => {
    setShowOtp(false)
    setOtp('')
    setTimer(OTP_VALID_DURATION)
    setOtpError(null)
    setIsHidden(false)
  }

  // The "edit your email" link inside the hint sentences (Trans fills in its text).
  const editEmailLink = <span className='ob-gate-edit' onClick={handleBackToEmail} role='button' tabIndex={0} />

  if (verified) {
    return (
      <div className='ob-step'>
        <StepTitle>{t('gate.title')}</StepTitle>
        <p className='ob-gate-verified'>
          <span className='ob-gate-verified-icon' aria-hidden='true'>
            <CheckIcon />
          </span>
          {email}
        </p>
      </div>
    )
  }

  return (
    <div className='ob-step'>
      {!showOtp ? (
        <>
          {/* The email is asked before anything has been earned, so the result
              tease, the trust strip and the fine print all go — it is only the
              question. */}
          <StepTitle>{t('gate.title')}</StepTitle>

          <form onSubmit={requestOtp} noValidate>
            <input
              type='email'
              inputMode='email'
              autoComplete='email'
              className='ob-input'
              placeholder={t('gate.placeholder')}
              value={email}
              maxLength={256}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailError(null)
              }}
            />
            {emailError && <p className='ob-error'>{emailError}</p>}
            <div className='ob-actions'>
              <button type='submit' className='ob-cta ob-cta--block' disabled={loading}>
                {loading ? t('gate.sending') : t('nav.continue')}
                {!loading && (
                  <svg width='18' height='18' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
                    <path d='M4 10H16M16 10L11 5M16 10L11 15' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </>
      ) : (
        <div className='ob-otp-wrap'>
          <OtpSection
            otp={otp}
            setOtp={setOtp}
            onClick={handleOtpSubmit}
            btnTitle={tookTest ? t('gate.submitResult') : t('gate.submitStart')}
            hideSubmit
            handleBackClick={handleBackToEmail}
            timer={timer}
            handleResend={handleResend}
            email={email}
            otpError={otpError}
            setOtpError={setOtpError}
            setIsHidden={setIsHidden}
            isHidden={isHidden}
          />
          <p className='ob-gate-hint'>
            {showSpamHint ? (
              <Trans t={t} i18nKey='gate.spamHint' components={{ edit: editEmailLink }} />
            ) : (
              <Trans t={t} i18nKey='gate.wrongEmail' components={{ edit: editEmailLink }} />
            )}
          </p>
        </div>
      )}
    </div>
  )
}

export default GateStep
